import {
  entryToIndication,
  IndicationSet,
  RolodexIndication,
} from 'features/indicationTriage'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { GeckoComponent, GeckoEntry, RolodexEntryType } from 'types'

import { useActiveLtaSet } from './useFfsIndicationSets'

interface UseFfsTriageDataArgs {
  component: GeckoComponent | undefined
  inspectionId: string | undefined
}

interface UseFfsTriageDataResult {
  /** LTA indication set for the (component, inspection) pair, if any. */
  ltaSet: IndicationSet | undefined
  /** Indications belonging to `ltaSet`, parsed from raw Rolodex entries. */
  indications: ReadonlyArray<RolodexIndication>
  /** True while EITHER the set query OR the indication-entry query is loading. */
  isLoading: boolean
  /** Refreshes BOTH the indication-set query and the indication-entries query. */
  forceRefresh: () => Promise<unknown>
}

/**
 * Fetches the LTA indication set + its indications for the FFS triage step.
 *
 * Owns its own indication-set query (rather than going through
 * `useIndicationSets`) so that `forceRefresh` can invalidate both the set
 * discovery AND the indication entries. The previous indirection through
 * `useIndicationSets` -- which uses the non-synced `useRolodexEntries` --
 * meant generating a new LTA set in the binning step would leave triage
 * stuck on the stale "no set" result until remount.
 */
export function useFfsTriageData({
  component,
  inspectionId,
}: UseFfsTriageDataArgs): UseFfsTriageDataResult {
  // 1. Resolve the active LTA set via the shared helper (used by
  //    `useFfsBinningValidation` too) — single source of truth for id
  //    scheme + scope semantics.
  const {
    ltaSet,
    isLoading: setsLoading,
    forceRefresh: refreshSets,
  } = useActiveLtaSet({ component, inspectionId })

  // 3. Query indications scoped to the current inspection.
  const {
    entries,
    isLoading: indicationsLoading,
    forceRefresh: refreshIndications,
  } = useRolodexEntriesWithSyncedRefreshes(
    inspectionId
      ? {
          type: RolodexEntryType.INDICATION,
          tags: { [RolodexEntryType.INSPECTION]: inspectionId },
        }
      : undefined
  )

  // 4. Parse entries through the canonical parser and filter to the active set.
  const indications = useMemo<ReadonlyArray<RolodexIndication>>(() => {
    if (!ltaSet) return []
    return (entries as ReadonlyArray<GeckoEntry>)
      .map((entry) => entryToIndication(entry))
      .filter((ind) => ind.indicationSetId === ltaSet.id)
  }, [entries, ltaSet])

  const forceRefresh = useCallback(async () => {
    await Promise.all([refreshSets(), refreshIndications()])
  }, [refreshSets, refreshIndications])

  return useMemo(
    () => ({
      ltaSet,
      indications,
      isLoading: setsLoading || indicationsLoading,
      forceRefresh,
    }),
    [ltaSet, indications, setsLoading, indicationsLoading, forceRefresh]
  )
}
